import { i18n } from "@/setup/i18n-setup";
import { printHumanFloat, printLocalDate } from "@/utils/pretty-print";
import { field, filterValueInput } from "@/utils/models/decorator-field";
import { model } from "@/utils/models/decorator-model";
import { SaleService } from "@/services/sale-service";
import { getEnumKeyFromValue } from "@/utils/enum";
import Bank from "./bank";
import Sale from "./sale";
import { v4 } from "uuid";
import { BankService } from "@/services";

// Negative values or items that are not present in the database
export enum SaleHistoryEventType {
	BankTransfer = -1,

	Edit = 1,
	Send = 2,
	Download = 3,
	Print = 4,
	Reminder = 5,
	SaleTypeChanged = 6,
	EmailRead = 7,
	ReminderEmailRead = 8,
	SentWithBillit = 9,
	Signed = 10,
	PeppolQueued = 11,
	PeppolSent = 12,
	PeppolReceiptReceived = 13,
	PeppolMlrReceived = 14,
	PeppolMaxRetriesExceeded = 15
}

@model("SaleHistory")
export default class SaleHistory{
	public readonly key:string = v4();

	@field("number")
	public ID:number = 0;

	@field("number")
	public SaleID: Number = 0;

	@field("date")
	public TimeStamp: Date = new Date();

	@field("number")
	public EventTypeID: SaleHistoryEventType = 1;

	@field("array")
	public Props: string[] = [];

	@field("number")
	@filterValueInput("BookYear")
	public BookYear: number = 0;

	@field("number")
	public UserID:number = 0;


	public constructor(data?: any) {
		if (!data) {
			return;
		}
		this.ID = data.ID;
		this.SaleID = data.SaleID;
		this.TimeStamp = new Date(data.TimeStamp);
		this.EventTypeID = data.EventTypeID;
		this.Props = data.Props || [];
		this.BookYear = data.BookYear;
		this.UserID = data.UserID;
	}

	public getJSON() {
		return {
			ID: this.ID,
			SaleID: this.SaleID,
			TimeStamp: this.TimeStamp.toJSON(),
			EventTypeID: this.EventTypeID,
			Props: this.Props,
			BookYear: this.BookYear,
			UserID: this.UserID
		};
	}

	public getTimeStampString(): string {
		return printLocalDate(this.TimeStamp);
	}

	public getEventString(): string {
		let key = getEnumKeyFromValue(SaleHistoryEventType, this.EventTypeID);
		if (this.EventTypeID == SaleHistoryEventType.SaleTypeChanged) {
			let originalType = SaleService.saleTypes.find(f=>`${f.ID}` == this.Props[0]);
			let originalName = originalType ? originalType.Name.getTranslation(i18n.locale) : "ERROR";
			let originalNumber = this.Props[1];
			let newType = SaleService.saleTypes.find(f=>`${f.ID}` == this.Props[2]);
			let newName = newType ? newType.Name.getTranslation(i18n.locale) : "ERROR";
			let newNumber = this.Props[3];
			return i18n.t("models.SaleHistoryEventType.SaleTypeChanged", {originalType: originalName, originalNumber, newType: newName, newNumber}).toString();
		}
		if (this.EventTypeID == SaleHistoryEventType.SentWithBillit){
			return i18n.t("models.SaleHistoryEventType.SentWithBillit", {id: this.Props[0]}).toString();
		}
		if (this.EventTypeID == SaleHistoryEventType.BankTransfer) {
			return i18n.t("models.SaleHistoryEventType.BankTransfer", {amount: this.Props[0], bankName: this.Props[1]}).toString();
		}
		return i18n.t(`models.SaleHistoryEventType.${key}`).toString();
	}

	public getEventProps(): any {
		if (this.EventTypeID == SaleHistoryEventType.Send) {
			if (this.Props)
				return this.Props[0];
		}
		if (this.EventTypeID == SaleHistoryEventType.Reminder) {
			if (this.Props)
				return i18n.t("views.SaleInfo.reminder", {num: this.Props[0]}).toString();
		}
		if (this.EventTypeID == SaleHistoryEventType.EmailRead && this.Props.length >= 1){
			return i18n.t("views.SaleInfo.document-read");
		}
		if (this.EventTypeID == SaleHistoryEventType.Signed && this.Props.length >= 1) {
			return i18n.t("views.SaleInfo.signed-by", {name: this.Props[1]});
		}
	}

	public hasExtraInfo():boolean {
		if (this.EventTypeID == SaleHistoryEventType.Reminder && this.Props.length >= 2){
			return true;
		}
		if (this.EventTypeID == SaleHistoryEventType.Send && this.Props.length >= 2) {
			return true;
		}
		if (this.EventTypeID == SaleHistoryEventType.EmailRead && this.Props.length >= 1) {
			return true;
		}
		if (this.EventTypeID == SaleHistoryEventType.Signed && this.Props.length >= 1) {
			return true;
		}
		return false;
	}

	public getExtraInfoComponentName():string{
		if (this.EventTypeID == SaleHistoryEventType.Reminder) {
			return "reminder-sent-info";
		}
		if (this.EventTypeID == SaleHistoryEventType.Send) {
			return "sent-info";
		}
		if (this.EventTypeID == SaleHistoryEventType.EmailRead) {
			return "email-read";
		}
		if (this.EventTypeID == SaleHistoryEventType.Signed) {
			return "signed";
		}
		return "";
	}

	public static fromBank(bank:Bank, sale:Sale):SaleHistory {
		let result = new SaleHistory();
		result.EventTypeID = SaleHistoryEventType.BankTransfer;
		result.SaleID = sale.ID;
		result.TimeStamp = bank.TimeCreated;
		result.BookYear = bank.BookYear;
		let bankTypeName = `bank ${bank.TypeID}`;
		let bankType = BankService.getType(bank.ID);
		if (bankType) {
			bankTypeName = bankType.getName();
		}
		result.Props = [printHumanFloat(bank.Total), bankTypeName];
		return result;
	}
}
